import * as XLSX from 'xlsx';
import { DashboardData } from '../../../types';
import {
  getDepartmentContentData,
  getOccupationContentData,
  getTimingContentData,
  orderedTimings,
  orderedResultTypes,
  aggregateReasonsByGrouping,
  recordWeight,
  UNFILLED_FIELD_LABEL,
  getNonComplianceReasonData,
  getReasonInputStats,
  ReasonAggregationGrouping,
  ReasonAggregationRow,
} from './dataProcessing';
import {
  buildSeriesByMetric,
  TimeSeriesMetric,
  XAxisGranularity,
} from './timeSeriesProcessing';

type TimeSeriesValueMode = 'ratioOnly' | 'ratioAndCount';

type ExcelExportOptions = {
  includeReasonSheets?: boolean;
  timeSeriesValueMode?: TimeSeriesValueMode;
  includeExtendedTimeSeriesSheets?: boolean;
};

const normalizeExcelExportOptions = (options?: boolean | ExcelExportOptions) => {
  if (typeof options === 'boolean') {
    return {
      includeReasonSheets: options,
      timeSeriesValueMode: 'ratioAndCount' as TimeSeriesValueMode,
      includeExtendedTimeSeriesSheets: options,
    };
  }

  return {
    includeReasonSheets: options?.includeReasonSheets ?? true,
    timeSeriesValueMode: options?.timeSeriesValueMode ?? 'ratioAndCount',
    includeExtendedTimeSeriesSheets: options?.includeExtendedTimeSeriesSheets ?? options?.includeReasonSheets ?? true,
  };
};

const timeSeriesMetricNameMap: Record<TimeSeriesMetric, string> = {
  resultBreakdown: '結果の内訳',
  nonComplianceReasonBreakdown: '未実施理由の内訳',
  nonComplianceOccupationBreakdown: '未実施理由の職種別内訳',
};

const timeSeriesGranularityNameMap: Record<XAxisGranularity, string> = {
  month: '月次',
  quarter: '四半期',
  year: '年次',
  fiscalYear: '年度',
};

  // 表データをエクセルに変換し、zipとしてダウンロードする関数
  export const getExcelBlob = (data: DashboardData, options?: boolean | ExcelExportOptions) => {
    if (!data) return null;

    const {
      includeReasonSheets,
      timeSeriesValueMode,
      includeExtendedTimeSeriesSheets,
    } = normalizeExcelExportOptions(options);

    // 部署データを取得
    const departmentData = getDepartmentContentData(data);

    // 職種データを取得
    const occupationData = getOccupationContentData(data);

    // ワークブックを作成
    const wb = XLSX.utils.book_new();

    // 部署別データをシートに追加
    const deptTableData = [
      ['部署', 'アルコールで実施した', '石鹸と流水で実施した', '実施しなかった', '合計', '遵守率(%)']
    ];
    departmentData.forEach(dept => {
      const total = (dept['アルコールで実施した'] || 0) +
                    (dept['石鹸と流水で実施した'] || 0) +
                    (dept['実施しなかった'] || 0);
      const complianceRate = total > 0 ?
                            (((dept['アルコールで実施した'] || 0) +
                              (dept['石鹸と流水で実施した'] || 0)) / total * 100).toFixed(1) : '--';
      deptTableData.push([
        dept.name,
        dept['アルコールで実施した'] || 0,
        dept['石鹸と流水で実施した'] || 0,
        dept['実施しなかった'] || 0,
        total,
        complianceRate
      ]);
    });
    const deptWs = XLSX.utils.aoa_to_sheet(deptTableData);
    XLSX.utils.book_append_sheet(wb, deptWs, '部署別');

    // 職種別データをシートに追加
    const occTableData = [
      ['職種', 'アルコールで実施した', '石鹸と流水で実施した', '実施しなかった', '合計', '遵守率(%)']
    ];
    occupationData.forEach(occ => {
      const total = (occ['アルコールで実施した'] || 0) +
                    (occ['石鹸と流水で実施した'] || 0) +
                    (occ['実施しなかった'] || 0);
      const complianceRate = total > 0 ?
                            (((occ['アルコールで実施した'] || 0) +
                              (occ['石鹸と流水で実施した'] || 0)) / total * 100).toFixed(1) : '--';
      occTableData.push([
        occ.name,
        occ['アルコールで実施した'] || 0,
        occ['石鹸と流水で実施した'] || 0,
        occ['実施しなかった'] || 0,
        total,
        complianceRate
      ]);
    });
    const occWs = XLSX.utils.aoa_to_sheet(occTableData);
    XLSX.utils.book_append_sheet(wb, occWs, '職種別');

    // タイミング別データをシートに追加
    const timingTableData = [
      ['タイミング', 'アルコールで実施した', '石鹸と流水で実施した', '実施しなかった', '合計', '遵守率(%)']
    ];

    // 既存の処理関数を使用してタイミング別データを取得
    const timingContentData = getTimingContentData(data);

    // 順序に従ってタイミング別データを処理
    timingContentData.forEach(timingData => {
      // 各結果タイプのカウントを取得
      const alcoholCount = timingData['アルコールで実施した'] || 0;
      const soapCount = timingData['石鹸と流水で実施した'] || 0;
      const noneCount = timingData['実施しなかった'] || 0;

      // 合計を計算
      const total = alcoholCount + soapCount + noneCount;

      // 遵守率を計算（アルコール + 石鹸と流水）/ 合計 * 100
      const complianceRate = total > 0 ? ((alcoholCount + soapCount) / total * 100).toFixed(1) : '--';

      timingTableData.push([
        timingData.name,
        alcoholCount,
        soapCount,
        noneCount,
        total,
        complianceRate
      ]);
    });

    const timingWs = XLSX.utils.aoa_to_sheet(timingTableData);
    XLSX.utils.book_append_sheet(wb, timingWs, 'タイミング別');

    // 詳細分析タブのデータを追加
    // 1. 部署×観察タイミング 用データの生成
    const generateDepartmentTimingData = () => {
      const deptTimingData: Record<string, Record<string, Record<string, number>>> = {};

      // 部署の一覧を取得
      const departments = departmentData.map(dept => dept.name);

      // 各部署に対して初期化
      departments.forEach(dept => {
        deptTimingData[dept] = {};
        orderedTimings.forEach(timing => {
          deptTimingData[dept][timing] = {};
          orderedResultTypes.forEach(result => {
            deptTimingData[dept][timing][result] = 0;
          });
        });
      });

      // raw_dataから各部署・タイミング・結果の組み合わせごとにカウント
      data.raw_data.forEach(record => {
        const dept = record.department || '未記入';
        const timing = record.timing;
        const result = record.result;

        // 該当するデータがある場合のみカウント
        if (dept && timing && result && deptTimingData[dept] && deptTimingData[dept][timing]) {
          if (!deptTimingData[dept][timing][result]) {
            deptTimingData[dept][timing][result] = 0;
          }
          deptTimingData[dept][timing][result] += recordWeight(record);
        }
      });

      return deptTimingData;
    };

    // 2. 職種×観察タイミング 用データの生成
    const generateOccupationTimingData = () => {
      const occTimingData: Record<string, Record<string, Record<string, number>>> = {};

      // 職種の一覧を取得
      const occupations = occupationData.map(occ => occ.name);

      // 各職種に対して初期化
      occupations.forEach(occ => {
        occTimingData[occ] = {};
        orderedTimings.forEach(timing => {
          occTimingData[occ][timing] = {};
          orderedResultTypes.forEach(result => {
            occTimingData[occ][timing][result] = 0;
          });
        });
      });

      // raw_dataから各職種・タイミング・結果の組み合わせごとにカウント
      data.raw_data.forEach(record => {
        const occ = record.occupation || '未記入';
        const timing = record.timing;
        const result = record.result;

        // 該当するデータがある場合のみカウント
        if (occ && timing && result && occTimingData[occ] && occTimingData[occ][timing]) {
          if (!occTimingData[occ][timing][result]) {
            occTimingData[occ][timing][result] = 0;
          }
          occTimingData[occ][timing][result] += recordWeight(record);
        }
      });

      return occTimingData;
    };

    // 3. 部署×職種×観察タイミング 用データの生成
    const generateDeptOccTimingData = () => {
      // 部署と職種のリストを取得
      const departments = Array.from(new Set(
        data.raw_data.filter(record => record.department).map(record => record.department)
      ));

      const occupations = Array.from(new Set(
        data.raw_data.filter(record => record.occupation).map(record => record.occupation)
      ));

      // 結果データ構造を初期化
      const resultData: Record<string, Record<string, Record<string, { implemented: number; total: number }> & { 合計: { implemented: number; total: number } }>> = {};

      // 部署別の合計データを追跡するためのオブジェクト
      const deptTotals: Record<string, { implemented: number; total: number }> = {};

      // 各部署の初期化
      departments.forEach(dept => {
        resultData[dept] = {} as Record<string, Record<string, { implemented: number; total: number }> & {
          合計: { implemented: number; total: number }
        }>;
        deptTotals[dept] = {
          implemented: 0,
          total: 0
        };

        // 各職種の初期化
        occupations.forEach(occ => {
          resultData[dept][occ] = {
            合計: { implemented: 0, total: 0 }
          } as Record<string, { implemented: number; total: number }> & {
            合計: { implemented: number; total: number }
          };

          // 各タイミングの初期化
          orderedTimings.forEach(timing => {
            resultData[dept][occ][timing] = {
              implemented: 0, // 実施数（アルコールまたは石鹸と流水）
              total: 0        // 合計数
            };
          });

          // 職種ごとの合計行の初期化
          resultData[dept][occ]['合計'] = {
            implemented: 0,
            total: 0
          };
        });
      });

      // raw_dataから集計
      data.raw_data.forEach(record => {
        const dept = record.department || '未記入';
        const occ = record.occupation || '未記入';
        const timing = record.timing;
        const recordResult = record.result;

        // 有効なデータのみ処理
        if (dept && occ && timing && recordResult &&
            resultData[dept] && resultData[dept][occ] && resultData[dept][occ][timing]) {

          const weight = recordWeight(record);
          // 職種ごとの合計カウントを増加
          resultData[dept][occ][timing].total += weight;
          resultData[dept][occ]['合計'].total += weight;

          // 部署合計も更新
          if (deptTotals[dept]) {
            deptTotals[dept].total += weight;
          }

          // アルコールまたは石鹸で実施した場合は実施カウントを増加
          if (recordResult === "アルコールで実施した" || recordResult === "石鹸と流水で実施した") {
            resultData[dept][occ][timing].implemented += weight;
            resultData[dept][occ]['合計'].implemented += weight;

            // 部署合計の実施数も更新
            if (deptTotals[dept]) {
              deptTotals[dept].implemented += weight;
            }
          }
        }
      });

      // 部署のデータに部署合計を追加
      departments.forEach(dept => {
        resultData[dept]['合計'] = {
          合計: { implemented: 0, total: 0 }
        } as Record<string, { implemented: number; total: number }> & {
          合計: { implemented: number; total: number }
        };

        // 各タイミングの部署合計を計算
        orderedTimings.forEach(timing => {
          resultData[dept]['合計'][timing] = {
            implemented: 0,
            total: 0
          };

          // 全職種からこのタイミングの合計を集計
          Object.keys(resultData[dept]).forEach(occ => {
            if (occ !== '合計' && resultData[dept][occ][timing]) {
              resultData[dept]['合計'][timing].implemented += resultData[dept][occ][timing].implemented;
              resultData[dept]['合計'][timing].total += resultData[dept][occ][timing].total;
            }
          });
        });

        // 部署全体の合計
        resultData[dept]['合計']['合計'] = {
          implemented: deptTotals[dept].implemented,
          total: deptTotals[dept].total
        };
      });

      return resultData;
    };

    const deptTimingData = generateDepartmentTimingData();
    const occTimingData = generateOccupationTimingData();
    const deptOccTimingData = generateDeptOccTimingData();

    // 部署×観察タイミング シートを追加
    const deptTimingTableData = [
      ['部署', '観察タイミング', 'アルコールで実施した', '石鹸と流水で実施した', '実施しなかった', '合計', '遵守率(%)']
    ];

    departmentData.forEach(deptRow => {
      const deptName = deptRow.name;

      // 各タイミングの行を追加
      orderedTimings.forEach(timing => {
        // この部署×タイミングの各結果のカウント
        const alcoholCount = deptTimingData[deptName]?.[timing]?.["アルコールで実施した"] || 0;
        const soapCount = deptTimingData[deptName]?.[timing]?.["石鹸と流水で実施した"] || 0;
        const noneCount = deptTimingData[deptName]?.[timing]?.["実施しなかった"] || 0;

        // 合計を計算
        const total = alcoholCount + soapCount + noneCount;

        // 遵守率を計算（アルコール + 石鹸と流水の合計 / 全体）
        const complianceRate = total > 0 ? ((alcoholCount + soapCount) / total * 100).toFixed(1) : '--';

        deptTimingTableData.push([
          deptName,
          timing,
          alcoholCount,
          soapCount,
          noneCount,
          total,
          complianceRate
        ]);
      });

      // 部署ごとの合計行
      const deptAlcoholTotal = orderedTimings.reduce((sum, timing) => {
        return sum + (deptTimingData[deptName]?.[timing]?.["アルコールで実施した"] || 0);
      }, 0);

      const deptSoapTotal = orderedTimings.reduce((sum, timing) => {
        return sum + (deptTimingData[deptName]?.[timing]?.["石鹸と流水で実施した"] || 0);
      }, 0);

      const deptNoneTotal = orderedTimings.reduce((sum, timing) => {
        return sum + (deptTimingData[deptName]?.[timing]?.["実施しなかった"] || 0);
      }, 0);

      const deptTotal = deptAlcoholTotal + deptSoapTotal + deptNoneTotal;
      const deptComplianceRate = deptTotal > 0 ? ((deptAlcoholTotal + deptSoapTotal) / deptTotal * 100).toFixed(1) : '--';

      deptTimingTableData.push([
        deptName,
        '合計',
        deptAlcoholTotal,
        deptSoapTotal,
        deptNoneTotal,
        deptTotal,
        deptComplianceRate
      ]);
    });

    const deptTimingWs = XLSX.utils.aoa_to_sheet(deptTimingTableData);
    XLSX.utils.book_append_sheet(wb, deptTimingWs, '部署×観察タイミング');

    // 職種×観察タイミング シートを追加
    const occTimingTableData = [
      ['職種', '観察タイミング', 'アルコールで実施した', '石鹸と流水で実施した', '実施しなかった', '合計', '遵守率(%)']
    ];

    occupationData.forEach(occRow => {
      const occName = occRow.name;

      // 各タイミングの行を追加
      orderedTimings.forEach(timing => {
        // この職種×タイミングの各結果のカウント
        const alcoholCount = occTimingData[occName]?.[timing]?.["アルコールで実施した"] || 0;
        const soapCount = occTimingData[occName]?.[timing]?.["石鹸と流水で実施した"] || 0;
        const noneCount = occTimingData[occName]?.[timing]?.["実施しなかった"] || 0;

        // 合計を計算
        const total = alcoholCount + soapCount + noneCount;

        // 遵守率を計算（アルコール + 石鹸と流水の合計 / 全体）
        const complianceRate = total > 0 ? ((alcoholCount + soapCount) / total * 100).toFixed(1) : '--';

        occTimingTableData.push([
          occName,
          timing,
          alcoholCount,
          soapCount,
          noneCount,
          total,
          complianceRate
        ]);
      });

      // 職種ごとの合計行
      const occAlcoholTotal = orderedTimings.reduce((sum, timing) => {
        return sum + (occTimingData[occName]?.[timing]?.["アルコールで実施した"] || 0);
      }, 0);

      const occSoapTotal = orderedTimings.reduce((sum, timing) => {
        return sum + (occTimingData[occName]?.[timing]?.["石鹸と流水で実施した"] || 0);
      }, 0);

      const occNoneTotal = orderedTimings.reduce((sum, timing) => {
        return sum + (occTimingData[occName]?.[timing]?.["実施しなかった"] || 0);
      }, 0);

      const occTotal = occAlcoholTotal + occSoapTotal + occNoneTotal;
      const occComplianceRate = occTotal > 0 ? ((occAlcoholTotal + occSoapTotal) / occTotal * 100).toFixed(1) : '--';

      occTimingTableData.push([
        occName,
        '合計',
        occAlcoholTotal,
        occSoapTotal,
        occNoneTotal,
        occTotal,
        occComplianceRate
      ]);
    });

    const occTimingWs = XLSX.utils.aoa_to_sheet(occTimingTableData);
    XLSX.utils.book_append_sheet(wb, occTimingWs, '職種×観察タイミング');

    // 部署×職種×観察タイミング シートを追加
    const deptOccTimingTableData = [
      ['部署', '職種', ...orderedTimings, '合計', '遵守率(%)']
    ];

    // 部署のソート関数（文字部分と数字部分を分離してソート）
    const sortDepartments = (a: string, b: string) => {
      const nameA = a || '';
      const nameB = b || '';

      // 正規表現で文字部分と数字部分を抽出
      const regexResult1 = nameA.match(/^([A-Za-z]+)(\d+)$/);
      const regexResult2 = nameB.match(/^([A-Za-z]+)(\d+)$/);

      // マッチしなかった場合（通常の文字列）は通常のソート
      if (!regexResult1 || !regexResult2) {
        return nameA.localeCompare(nameB);
      }

      // 文字部分と数字部分を取得
      const [, letterA, numberA] = regexResult1;
      const [, letterB, numberB] = regexResult2;

      // まず文字部分で比較
      const letterCompare = letterA.localeCompare(letterB);
      if (letterCompare !== 0) {
        return letterCompare;
      }

      // 文字部分が同じなら、数字部分を数値として比較
      return parseInt(numberA, 10) - parseInt(numberB, 10);
    };

    // 部署を名称順にソート
    const departmentsBySortOrder = Object.keys(deptOccTimingData).sort(sortDepartments);

    departmentsBySortOrder.forEach(dept => {
      // 職種を名称順でソート
      const occupations = Object.keys(deptOccTimingData[dept])
        .filter(occ => occ !== '合計')
        .sort((a, b) => a.localeCompare(b));

      occupations.forEach(occ => {
        const rowData = [dept, occ];

        // 各タイミングの実施・合計データを追加
        orderedTimings.forEach(timing => {
          const timingData = deptOccTimingData[dept][occ][timing] || {implemented: 0, total: 0};
          rowData.push(`${timingData.implemented}/${timingData.total}`);
        });

        // 合計列
        const totalData = deptOccTimingData[dept][occ]['合計'];
        rowData.push(`${totalData.implemented}/${totalData.total}`);

        // 遵守率
        const complianceRate = totalData.total === 0
          ? "--"
          : (totalData.implemented / totalData.total * 100).toFixed(1);

        rowData.push(complianceRate);

        deptOccTimingTableData.push(rowData);
      });

      // 部署合計行
      const deptTotalRow = [dept, '合計'];

      // 各タイミングの合計
      orderedTimings.forEach(timing => {
        const deptTimingTotal = deptOccTimingData[dept]['合計'][timing] || {implemented: 0, total: 0};
        deptTotalRow.push(`${deptTimingTotal.implemented}/${deptTimingTotal.total}`);
      });

      // 部署全体の合計
      const deptTotal = deptOccTimingData[dept]['合計']['合計'];
      deptTotalRow.push(`${deptTotal.implemented}/${deptTotal.total}`);

      // 部署全体の遵守率
      const deptComplianceRate = deptTotal.total === 0
        ? "--"
        : (deptTotal.implemented / deptTotal.total * 100).toFixed(1);

      deptTotalRow.push(deptComplianceRate);

      deptOccTimingTableData.push(deptTotalRow);
    });

    const deptOccTimingWs = XLSX.utils.aoa_to_sheet(deptOccTimingTableData);
    XLSX.utils.book_append_sheet(wb, deptOccTimingWs, '部署×職種×観察タイミング');

    const createTimeSeriesSheetData = (
      metric: TimeSeriesMetric,
      granularity: XAxisGranularity,
      valueMode: TimeSeriesValueMode
    ) => {
      const { series, seriesKeys } = buildSeriesByMetric(metric, granularity, data, {
        allowShortRange: true,
      });

      const header: string[] = ['期間'];
      seriesKeys.forEach((key) => {
        header.push(`${key}割合(%)`);
        if (valueMode === 'ratioAndCount') {
          header.push(`${key}件数`);
        }
      });

      const rows: (string | number)[][] = [header];
      series.forEach((bucket) => {
        const row: (string | number)[] = [bucket.label];
        seriesKeys.forEach((key) => {
          row.push((bucket[key] as number) || 0);
          if (valueMode === 'ratioAndCount') {
            row.push((bucket[`${key}Count`] as number) || 0);
          }
        });
        rows.push(row);
      });

      return rows;
    };

    const timeSeriesMetrics: TimeSeriesMetric[] = ['resultBreakdown'];
    if (includeExtendedTimeSeriesSheets) {
      timeSeriesMetrics.push('nonComplianceReasonBreakdown', 'nonComplianceOccupationBreakdown');
    }

    const granularities: XAxisGranularity[] = ['month', 'quarter', 'year', 'fiscalYear'];
    timeSeriesMetrics.forEach((metric) => {
      granularities.forEach((granularity) => {
        const tableData = createTimeSeriesSheetData(metric, granularity, timeSeriesValueMode);
        const ws = XLSX.utils.aoa_to_sheet(tableData);
        const sheetName = `時系列_${timeSeriesMetricNameMap[metric]}_${timeSeriesGranularityNameMap[granularity]}`;
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
    });

    if (includeReasonSheets) {
      // 未実施理由データを追加
      const reasonStats = getReasonInputStats(data);
      const reasonSummary = getNonComplianceReasonData(data);
      const reasonOrder = reasonSummary.map(reason => reason.name);

      // 未実施理由概要シート
      const reasonSummaryTableData: (string | number)[][] = [
        ['指標', '値'],
        ['未実施件数', reasonStats.totalNonCompliance],
        ['理由入力あり', reasonStats.withReasonCount],
        ['理由未入力', reasonStats.withoutReasonCount],
        ['理由入力率(%)', Number(reasonStats.reasonInputRate.toFixed(1))],
        [],
        ['理由', '件数'],
      ];

      reasonSummary.forEach(reason => {
        reasonSummaryTableData.push([reason.name, reason.value]);
      });

      const reasonSummaryWs = XLSX.utils.aoa_to_sheet(reasonSummaryTableData);
      XLSX.utils.book_append_sheet(wb, reasonSummaryWs, '未実施理由_概要');

      // 未実施理由の各種クロス集計シート
      const reasonGroupings: { grouping: ReasonAggregationGrouping; sheetName: string; headers: string[] }[] = [
        { grouping: 'department', sheetName: '未実施理由_部署別', headers: ['部署'] },
        { grouping: 'occupation', sheetName: '未実施理由_職種別', headers: ['職種'] },
        { grouping: 'timing', sheetName: '未実施理由_タイミング別', headers: ['観察タイミング'] },
        { grouping: 'departmentOccupation', sheetName: '未実施理由_部署×職種', headers: ['部署', '職種'] },
        { grouping: 'departmentTiming', sheetName: '未実施理由_部署×観察タイミング', headers: ['部署', '観察タイミング'] },
        { grouping: 'departmentOccupationTiming', sheetName: '未実施理由_部署×職種×観察タイミング', headers: ['部署', '職種', '観察タイミング'] },
      ];

      const splitGroupName = (groupName: string, expectedParts: number) => {
        const parts = groupName.split('×');
        return Array.from({ length: expectedParts }, (_, index) => parts[index]?.trim() || UNFILLED_FIELD_LABEL);
      };

      const collator = new Intl.Collator('ja', { numeric: true, sensitivity: 'base' });

      const getTimingOrder = (timing: string) => {
        const index = orderedTimings.indexOf(timing);
        return index === -1 ? orderedTimings.length : index;
      };

      const sortReasonAggregationRows = (rows: ReasonAggregationRow[], grouping: ReasonAggregationGrouping, headers: string[]) => {
        if (!['departmentOccupation', 'departmentTiming', 'departmentOccupationTiming'].includes(grouping)) {
          return rows;
        }

        const compareWithTimingOrder = (timingA: string, timingB: string) => {
          const orderDiff = getTimingOrder(timingA) - getTimingOrder(timingB);
          return orderDiff !== 0 ? orderDiff : collator.compare(timingA, timingB);
        };

        return [...rows].sort((a, b) => {
          const aValues = splitGroupName(a.name, headers.length);
          const bValues = splitGroupName(b.name, headers.length);

          const deptCompare = collator.compare(aValues[0], bValues[0]);
          if (deptCompare !== 0) return deptCompare;

          if (grouping === 'departmentOccupation') {
            return collator.compare(aValues[1], bValues[1]);
          }

          if (grouping === 'departmentTiming') {
            return compareWithTimingOrder(aValues[1], bValues[1]);
          }

          const occupationCompare = collator.compare(aValues[1], bValues[1]);
          if (occupationCompare !== 0) return occupationCompare;

          return compareWithTimingOrder(aValues[2], bValues[2]);
        });
      };

      reasonGroupings.forEach(({ grouping, sheetName, headers }) => {
        const aggregation = aggregateReasonsByGrouping(data, grouping, reasonOrder);
        const tableData: (string | number)[][] = [];

        const header = [...headers, ...aggregation.reasons, '合計'];
        tableData.push(header);

        const sortedData = sortReasonAggregationRows(aggregation.data, grouping, headers);

        sortedData.forEach(row => {
          const groupValues = splitGroupName(row.name, headers.length);
          const rowData: (string | number)[] = [...groupValues];
          aggregation.reasons.forEach(reason => {
            rowData.push((row[reason] as number) || 0);
          });
          rowData.push(row.total);
          tableData.push(rowData);
        });

        const ws = XLSX.utils.aoa_to_sheet(tableData);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
    }

    // blobとして出力
    const wopts: XLSX.WritingOptions = { bookType: 'xlsx', bookSST: false, type: 'array' as 'array' };
    const wbout = XLSX.write(wb, wopts);

    return new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  };
